import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";

import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { API_URL } from "../../constants";
import { FREE_MONTHLY_CREDITS, formatCreditsNumber } from "../../constants/credits";
import { authHeader, setToken } from "../../utils/auth";
import { storageGet, storageSet } from "../../utils/storage";

// One-time "add a password" nudge after a code sign-in (D8: passwords are the
// default method now). A single dismissal is remembered for good — same
// pattern as liq_profile_banner_dismissed.
// Per-account, not global: on a shared device one agent's "Skip for now"
// must not silence the nudge for every other passwordless account.
const passwordPromptDismissedKey = (/** @type {string} */ email) =>
  `liq_password_prompt_dismissed:${(email || "").trim().toLowerCase()}`;

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 140px 0 80px;
`;

const Card = styled.div`
  max-width: 420px;
  margin: 0 auto;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 1.9rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #4a4a4a;
  margin: 0 0 28px 0;
`;

// One framing line under the sign-in subtitle once credits are live: what a
// new account starts with. A fact, not a pitch.
const CreditsNote = styled.p`
  font-size: 0.92rem;
  color: #6b7280;
  margin: -16px 0 24px 0;
`;

const Field = styled.input`
  width: 100%;
  font-size: 1rem;
  color: #1a1a1a;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
  font-family: inherit;
  text-align: center;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
    box-shadow: 0 0 0 3px rgba(26, 26, 26, 0.12);
  }
`;

const CodeField = styled(Field)`
  font-size: 1.5rem;
  letter-spacing: 8px;
  font-weight: 600;
`;

const SubmitButton = styled.button`
  width: 100%;
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  background: var(--brand-primary, #0e7e7a);
  border: 1px solid #1a1a1a;
  border-radius: 999px;
  padding: 12px 24px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--brand-primary-700, #0b6360);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 18px;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.9rem;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    color: #9ca3af;
    text-decoration: none;
    cursor: default;
  }
`;

const ErrorText = styled.p`
  color: #b91c1c;
  font-size: 0.9rem;
  margin: 0 0 14px 0;
`;

/**
 * @param {unknown} err
 * @param {string} fallback
 */
const serverError = (err, fallback) => {
  const axiosErr = /** @type {{ response?: { data?: { error?: string } } }} */ (err);
  return axiosErr.response?.data?.error || fallback;
};

const LoginPage = () => {
  const history = useHistory();
  const location = useLocation();
  // Optional return destination (?next=/reports/…/edit) so an agent who came
  // from a prep link lands back on it after signing in. Internal paths only —
  // anything else falls back to the dashboard.
  const rawNext = new URLSearchParams(location.search).get("next") || "";
  const nextPath = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/dashboard";
  // Password sign-in is the default (D8); "email" -> "code" is the emailed
  // one-time-code flow, which stays fully working — it is the path for
  // accounts without a password, the path that CREATES accounts, and the
  // password-recovery path. "setPassword" is the post-code-sign-in nudge.
  const [step, setStep] = useState(
    /** @type {"password" | "email" | "code" | "setPassword"} */ ("password")
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  // Tick down the resend cooldown once per second.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const finishSignIn = useCallback(() => {
    history.push(nextPath);
  }, [history, nextPath]);

  const submitPassword = useCallback(async () => {
    if (!email.trim() || !password) return;
    setBusy(true);
    setError("");
    try {
      const resp = await axios.post(`${API_URL}/auth/login/`, {
        email: email.trim(),
        password,
      });
      setToken(resp.data.token);
      finishSignIn();
    } catch (err) {
      // The server's 401 is deliberately uniform ("That email and password
      // don't match.") — show it verbatim, never something more specific.
      setError(serverError(err, "Couldn't sign in. Try again."));
    } finally {
      setBusy(false);
    }
  }, [email, password, finishSignIn]);

  const requestCode = useCallback(async () => {
    if (!email.trim()) return;
    setBusy(true);
    setError("");
    try {
      const resp = await axios.post(`${API_URL}/auth/request-code/`, { email: email.trim() });
      setStep("code");
      // Start (or resume) the resend countdown the server reports.
      setCooldown(resp.data?.retry_after_seconds || 30);
    } catch (err) {
      // A 429 means the sender cap fired — the address is fine and telling
      // the user to "check" it sends them the wrong way; the fix is to wait.
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      setError(
        status === 429
          ? "Too many code requests. Wait a minute, then try again."
          : "Couldn't send a code. Check the address and try again."
      );
    } finally {
      setBusy(false);
    }
  }, [email]);

  const verifyCode = useCallback(async () => {
    if (!code.trim()) return;
    setBusy(true);
    setError("");
    try {
      const resp = await axios.post(`${API_URL}/auth/verify-code/`, {
        email: email.trim(),
        code: code.trim(),
      });
      setToken(resp.data.token);
      // Nudge code-only accounts toward the default sign-in method, once:
      // skippable, and a single dismissal is remembered forever.
      if (
        resp.data.customer?.password_set === false &&
        storageGet(passwordPromptDismissedKey(email), "local") !== "1"
      ) {
        setError("");
        setStep("setPassword");
      } else {
        finishSignIn();
      }
    } catch (err) {
      setError(serverError(err, "That code didn't work. Try again."));
    } finally {
      setBusy(false);
    }
  }, [code, email, finishSignIn]);

  const savePassword = useCallback(async () => {
    if (newPassword.length < 10) {
      setError("Use at least 10 characters.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const resp = await axios.post(
        `${API_URL}/auth/set-password/`,
        { password: newPassword },
        { headers: authHeader() }
      );
      // Setting a password revokes every outstanding session — including this
      // one — and returns its replacement; store it before navigating.
      if (resp.data?.token) setToken(resp.data.token);
      finishSignIn();
    } catch (err) {
      setError(serverError(err, "Couldn't save that password. Try again."));
    } finally {
      setBusy(false);
    }
  }, [newPassword, finishSignIn]);

  const skipPassword = useCallback(() => {
    storageSet(passwordPromptDismissedKey(email), "1", "local");
    finishSignIn();
  }, [email, finishSignIn]);

  const toCodeFlow = useCallback(() => {
    setStep("email");
    setError("");
  }, []);

  return (
    <PageContainer>
      <NewNavbar />
      <ContentWrapper>
        <Container>
          <Card>
            {step === "password" && (
              <>
                <Title>Sign in or create your account</Title>
                <Subtitle>
                  Welcome back — sign in with your password. New here? We'll email you a code to
                  create your account.
                </Subtitle>
                <CreditsNote>
                  New accounts start with {formatCreditsNumber(FREE_MONTHLY_CREDITS)} free credits
                  every month.
                </CreditsNote>
                {error && <ErrorText>{error}</ErrorText>}
                <Field
                  type="email"
                  aria-label="Email address"
                  placeholder="you@brokerage.com"
                  value={email}
                  disabled={busy}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Field
                  type="password"
                  aria-label="Password"
                  placeholder="Your password"
                  value={password}
                  disabled={busy}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitPassword();
                  }}
                />
                <SubmitButton
                  type="button"
                  onClick={submitPassword}
                  disabled={busy || !email.trim() || !password}
                >
                  {busy ? "Signing in…" : "Sign in"}
                </SubmitButton>
                <ActionRow>
                  <TextButton type="button" onClick={toCodeFlow}>
                    Email me a code instead
                  </TextButton>
                </ActionRow>
              </>
            )}
            {step === "email" && (
              <>
                <Title>Email me a code</Title>
                <Subtitle>
                  We'll send a one-time sign-in code — no password needed. New accounts are created
                  here too.
                </Subtitle>
                {error && <ErrorText>{error}</ErrorText>}
                <Field
                  type="email"
                  aria-label="Email address"
                  placeholder="you@brokerage.com"
                  value={email}
                  disabled={busy}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") requestCode();
                  }}
                />
                <SubmitButton type="button" onClick={requestCode} disabled={busy || !email.trim()}>
                  {busy ? "Sending…" : "Send code"}
                </SubmitButton>
                <ActionRow>
                  <TextButton
                    type="button"
                    onClick={() => {
                      setStep("password");
                      setError("");
                    }}
                  >
                    Use your password instead
                  </TextButton>
                </ActionRow>
              </>
            )}
            {step === "code" && (
              <>
                <Title>Enter your code</Title>
                <Subtitle>We emailed a 6-digit code to {email}. It expires in 1 hour.</Subtitle>
                {error && <ErrorText>{error}</ErrorText>}
                <CodeField
                  type="text"
                  aria-label="6-digit sign-in code"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  disabled={busy}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") verifyCode();
                  }}
                />
                <SubmitButton type="button" onClick={verifyCode} disabled={busy || !code.trim()}>
                  {busy ? "Verifying…" : "Sign in"}
                </SubmitButton>
                <ActionRow>
                  <TextButton type="button" onClick={requestCode} disabled={busy || cooldown > 0}>
                    {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
                  </TextButton>
                  <TextButton
                    type="button"
                    onClick={() => {
                      setStep("email");
                      setCode("");
                      setError("");
                      setCooldown(0);
                    }}
                  >
                    Use a different email
                  </TextButton>
                </ActionRow>
              </>
            )}
            {step === "setPassword" && (
              <>
                <Title>Add a password</Title>
                <Subtitle>
                  You're signed in. Add a password to make it the fastest way back in — you can
                  always email yourself a code instead.
                </Subtitle>
                {error && <ErrorText>{error}</ErrorText>}
                <Field
                  type="password"
                  aria-label="New password"
                  placeholder="At least 10 characters"
                  value={newPassword}
                  disabled={busy}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") savePassword();
                  }}
                />
                <SubmitButton type="button" onClick={savePassword} disabled={busy || !newPassword}>
                  {busy ? "Saving…" : "Save password"}
                </SubmitButton>
                <ActionRow>
                  <TextButton type="button" onClick={skipPassword} disabled={busy}>
                    Skip for now
                  </TextButton>
                </ActionRow>
              </>
            )}
          </Card>
        </Container>
      </ContentWrapper>
      <NewFooter />
    </PageContainer>
  );
};

export default LoginPage;
